export const ACCESS_TOKEN_KEY = '__access_token__'
export const REFRESH_TOKEN_KEY = '__refresh_token__'
export const DEVICE_TOKEN_KEY = '__device_token__'
export const LOGIN_IN_PROGRESS_KEY = '__login_in_progress__'
export const LOGOUT_ENFORCED_KEY = '__logout_enforced__'

export const DEFAULT_PER_PAGE = 10

export const FILTER_CONTROL_WIDTH = 180

export const TABLE_COLUMNS_COUNT = 5

const AUTH_PATH = 'authentication/'
export const LOGIN_URL = `/${AUTH_PATH}login`
export const APPLICATIONS_URL = '/applications'

/**
 * Which deployment this build targets.
 *
 * account-ui tags its build env as `account-development` / `account-production` —
 * the prefix keeps its ClickUp support tickets isolated from the ebs platform
 * (see `src/features/tickets`) — so the bare name has to be recovered before it
 * can be compared.
 *
 * `toUnleashEnvironment` (`src/feature-flags/config.ts`) and
 * `toAnalyticsEnvironment` (`src/analytics/config.ts`) already each carry their
 * own copy of this line; this is the third. `constants.ts` — imported almost
 * everywhere and importing nothing — is where the three should consolidate, but
 * the other two are left alone here rather than rewired as a side effect of a
 * docs-link change.
 */
const APP_ENVIRONMENT = (
  import.meta.env?.VITE_APP_ENV ?? 'development'
).replace(/^account-/, '')

/**
 * Public documentation.
 *
 * One origin in one place, and it follows the environment: the docs are deployed
 * per environment, `skalx.dev` for development and `skalx.ai` for production, so
 * a hard-coded origin sends one of the two audiences to the wrong site. Anything
 * that is not production gets the development docs — the same posture as every
 * other consumer of `VITE_APP_ENV` here.
 *
 * New links belong in `DOCS_LINKS` rather than in the component that needs them.
 *
 * Only the origin differs between the two deployments — the paths below are the
 * same on both, and every one is verified to resolve. They do not share a tidy
 * prefix — the identity pages sit under `/skalx/core/…` while workspace settings
 * sits under `/core/core/…` — so each entry carries its whole path.
 */
export const DOCS_ORIGIN =
  APP_ENVIRONMENT === 'production'
    ? 'https://skalx.ai/docs'
    : 'https://skalx.dev/docs'

/** Marketing site the docs and legal pages live under. */
export const SITE_ORIGIN =
  APP_ENVIRONMENT === 'production' ? 'https://skalx.ai' : 'https://skalx.dev'

/** Legal pages, served from the marketing site alongside the docs. */
export const PRIVACY_URL = `${SITE_ORIGIN}/privacy-and-policy`

export const DOCS_LINKS = {
  users: `${DOCS_ORIGIN}/skalx/core/identity/users`,
  rolesPermissions: `${DOCS_ORIGIN}/skalx/core/identity/roles-permissions`,
  authentication: `${DOCS_ORIGIN}/skalx/core/identity/authentication`,
  signInMethods: `${DOCS_ORIGIN}/skalx/core/identity/authentication#sign-in-methods`,
  customAuthProviders: `${DOCS_ORIGIN}/skalx/core/identity/authentication#custom-providers`,
  addCustomAuthProvider: `${DOCS_ORIGIN}/skalx/core/identity/authentication#adding-a-custom-provider`,
  workspaceSettings: `${DOCS_ORIGIN}/core/core/workspace-settings`,
  applications: `${DOCS_ORIGIN}/core/core/applications`,
  accountProfile: `${DOCS_ORIGIN}/core/account-settings/overview#identity-details`,
  accountSecurity: `${DOCS_ORIGIN}/core/account-settings/security`
} as const
